import { StatCard } from '../StatCard.js';

/**
 * ClassicTemplate — the primary "My Running Resume" card.
 *
 * Centered layout: photo on top, name centered, stats below name,
 * race highlights as cards with colored tags and achievement badges.
 */
export class ClassicTemplate {
  static render({ resume, stats, races }) {
    const card = document.createElement('div');
    card.className = 'tpl-classic';

    // --- Header bar ---
    const header = document.createElement('div');
    header.className = 'tpl-classic__header';
    const headerLeft = document.createElement('span');
    headerLeft.className = 'tpl-classic__header-left';
    headerLeft.textContent = 'MY RUNNING RESUME';
    header.append(headerLeft);

    // --- Identity section (centered) ---
    const identity = document.createElement('div');
    identity.className = 'tpl-classic__identity';

    // Profile image (centered)
    const imgWrap = document.createElement('div');
    imgWrap.className = 'tpl-classic__img-wrap';

    if (resume.profileImageUrl && /^https?:\/\//.test(resume.profileImageUrl)) {
      const img = document.createElement('img');
      img.className = 'tpl-classic__img';
      img.src = resume.profileImageUrl;
      img.alt = resume.name || 'Profile';
      imgWrap.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'tpl-classic__img-placeholder';
      placeholder.innerHTML = `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;
      imgWrap.appendChild(placeholder);
    }

    // Name block (centered)
    const nameBlock = document.createElement('div');
    nameBlock.className = 'tpl-classic__name-block';

    const nameParts = (resume.name || 'Your Name').trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    const nameEl = document.createElement('div');
    nameEl.className = 'tpl-classic__name';
    nameEl.appendChild(document.createTextNode(firstName.toUpperCase() + ' '));
    const lastNameStrong = document.createElement('strong');
    lastNameStrong.textContent = lastName.toUpperCase();
    nameEl.appendChild(lastNameStrong);

    const subtitleEl = document.createElement('div');
    subtitleEl.className = 'tpl-classic__subtitle';
    subtitleEl.textContent = resume.subtitle || 'Runner';

    nameBlock.append(nameEl, subtitleEl);
    identity.append(imgWrap, nameBlock);

    // --- Stats row ---
    const statsRow = document.createElement('div');
    statsRow.className = 'tpl-classic__stats';

    statsRow.appendChild(StatCard.render({
      label: 'TOTAL MILEAGE', value: stats.formattedTotalDistance, unit: 'KM',
      accentColor: 'var(--cyan)',
    }));

    statsRow.appendChild(StatCard.render({
      label: 'RACES', value: String(races.length), unit: 'TOTAL',
      accentColor: 'var(--green)',
    }));

    if (stats.totalElevation > 0) {
      statsRow.appendChild(StatCard.render({
        label: 'ELEVATION GAIN', value: stats.totalElevation.toLocaleString(), unit: 'METERS',
        accentColor: '#2dd4bf',
      }));
    }

    const prEntry = ClassicTemplate._findBestPR(stats);
    if (prEntry) {
      statsRow.appendChild(StatCard.render({
        label: `PR (${prEntry.category})`,
        value: ClassicTemplate._formatPRTime(prEntry.time),
        unit: 'HR / MIN',
        accentColor: 'var(--gold)',
      }));
    }

    // --- Race Highlights ---
    const logSection = document.createElement('div');
    logSection.className = 'tpl-classic__log';

    const logTitle = document.createElement('div');
    logTitle.className = 'tpl-classic__log-title';
    logTitle.textContent = 'RACE HIGHLIGHTS';

    logSection.append(logTitle);

    if (races.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'tpl-classic__log-empty';
      empty.textContent = 'No races yet — add your first race!';
      logSection.appendChild(empty);
    } else {
      const list = document.createElement('div');
      list.className = 'tpl-classic__log-list';

      const sorted = [...races].sort((a, b) =>
        ClassicTemplate._parseDate(b.date) - ClassicTemplate._parseDate(a.date)
      );

      let currentYear = null;
      sorted.forEach((race) => {
        const year = ClassicTemplate._extractYear(race.date);
        if (year && year !== currentYear) {
          currentYear = year;
          const yearHeader = document.createElement('div');
          yearHeader.className = 'tpl-classic__year-header';
          yearHeader.textContent = year;
          list.appendChild(yearHeader);
        }
        list.appendChild(ClassicTemplate._renderRaceRow(race));
      });

      logSection.appendChild(list);
    }

    card.append(header, identity, statsRow, logSection);
    return card;
  }

  // ---------------------------------------------------------------------------
  // Race row — card style with colored left border and tag badges
  // ---------------------------------------------------------------------------

  static _renderRaceRow(race) {
    const row = document.createElement('div');
    row.className = 'tpl-classic__race-row';

    // Colored left border
    if (race.isPodium) {
      row.classList.add('tpl-classic__race-row--podium');
    } else if (race.isPR) {
      row.classList.add('tpl-classic__race-row--pr');
    } else if (race.type === 'trail') {
      row.classList.add('tpl-classic__race-row--trail');
    }

    // Left block
    const left = document.createElement('div');
    left.className = 'tpl-classic__race-left';

    // Name (clickable if resultsLink exists) + badges
    const nameRow = document.createElement('div');
    nameRow.className = 'tpl-classic__race-name';

    if (race.resultsLink && /^https?:\/\//.test(race.resultsLink)) {
      const link = document.createElement('a');
      link.href = race.resultsLink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'tpl-classic__race-link';
      link.textContent = race.raceName;
      nameRow.appendChild(link);
    } else {
      nameRow.textContent = race.raceName;
    }

    if (race.isPR) {
      const prBadge = document.createElement('span');
      prBadge.className = 'tpl-classic__pr-badge';
      prBadge.textContent = 'PR';
      nameRow.appendChild(prBadge);
    }

    if (race.isPodium) {
      const podiumBadge = document.createElement('span');
      podiumBadge.className = 'tpl-classic__podium-badge';
      podiumBadge.textContent = '🏆 Podium';
      nameRow.appendChild(podiumBadge);
    }

    // Detail row with tag badges
    const detailRow = document.createElement('div');
    detailRow.className = 'tpl-classic__race-detail';

    // Type tag
    const typeTag = document.createElement('span');
    typeTag.className = `tpl-classic__tag tpl-classic__tag--${race.type}`;
    typeTag.textContent = race.type === 'trail' ? 'Trail' : 'Road';
    detailRow.appendChild(typeTag);

    // Distance
    if (race.distance) {
      const distTag = document.createElement('span');
      distTag.className = 'tpl-classic__detail-text';
      distTag.textContent = `${race.distance} KM`;
      detailRow.appendChild(distTag);
    }

    // Elevation for trail
    if (race.type === 'trail' && race.elevation) {
      const elevTag = document.createElement('span');
      elevTag.className = 'tpl-classic__tag tpl-classic__tag--elev';
      elevTag.textContent = `↑${race.elevation}m`;
      detailRow.appendChild(elevTag);
    }

    // Location + Date as plain text
    const metaParts = [];
    if (race.location) metaParts.push(race.location);
    const dateStr = ClassicTemplate._formatDate(race.date);
    if (dateStr) metaParts.push(dateStr);
    if (metaParts.length) {
      const metaText = document.createElement('span');
      metaText.className = 'tpl-classic__detail-text';
      metaText.textContent = '· ' + metaParts.join(' · ');
      detailRow.appendChild(metaText);
    }

    left.append(nameRow, detailRow);

    // Notes (optional, shown as italic text below details)
    if (race.notes) {
      const notesEl = document.createElement('div');
      notesEl.className = 'tpl-classic__race-notes';
      notesEl.textContent = `"${race.notes}"`;
      left.appendChild(notesEl);
    }

    // Right block: time + achievement badge
    const right = document.createElement('div');
    right.className = 'tpl-classic__race-right';

    if (race.time) {
      const isDNF = race.time.toUpperCase() === 'DNF';
      const timeEl = document.createElement('div');
      timeEl.className = 'tpl-classic__race-time';
      if (isDNF) {
        timeEl.classList.add('tpl-classic__race-time--dnf');
        timeEl.textContent = 'DNF';
      } else {
        timeEl.textContent = race.time;
      }
      right.appendChild(timeEl);

      const achievement = ClassicTemplate._achievementLabel(race);
      if (achievement) {
        const achEl = document.createElement('div');
        achEl.className = 'tpl-classic__race-achievement';
        if (race.isPodium) achEl.classList.add('tpl-classic__race-achievement--podium');
        else if (race.isPR) achEl.classList.add('tpl-classic__race-achievement--pr');
        if (isDNF) achEl.classList.add('tpl-classic__race-achievement--dnf');
        achEl.textContent = achievement;
        right.appendChild(achEl);
      }
    }

    row.append(left, right);
    return row;
  }

  static _achievementLabel(race) {
    if (race.time && race.time.toUpperCase() === 'DNF') return 'DID NOT FINISH';
    if (race.isPodium) return 'PODIUM FINISH';
    if (race.isPR) return 'PERSONAL RECORD';
    if (race.type === 'trail') return 'FINISHER';
    if (race.distance >= 41.5) return 'FINISHER';
    return null;
  }

  static _findBestPR(stats) {
    if (!stats.prByDistance || stats.prByDistance.size === 0) return null;
    const preferred = ['Marathon', 'Half Marathon', 'Ultra', '10K', '5K'];
    for (const cat of preferred) {
      if (stats.prByDistance.has(cat)) return { category: cat, ...stats.prByDistance.get(cat) };
    }
    const [category, data] = stats.prByDistance.entries().next().value;
    return { category, ...data };
  }

  static _formatPRTime(timeStr) {
    if (!timeStr) return '--';
    const parts = timeStr.split(':');
    if (parts.length !== 3) return timeStr;
    const h = parseInt(parts[0], 10);
    const m = parts[1];
    if (h > 0) return `${h}:${m}`;
    return `${parseInt(m, 10)}:${parts[2]}`;
  }

  static _parseDate(dateStr) {
    if (!dateStr) return 0;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }

  static _extractYear(dateStr) {
    if (!dateStr) return null;
    const match = String(dateStr).match(/(\d{4})/);
    return match ? match[1] : null;
  }

  static _formatDate(dateStr) {
    if (!dateStr) return '';
    const str = String(dateStr);
    if (/^\d{4}$/.test(str)) return str;
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  }
}
